import React, { useCallback, useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "reactstrap";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDays, faTableColumns } from "@fortawesome/free-solid-svg-icons";
import { InputWembii } from "../../InputWembii";
import { useIntl } from "react-intl";
import CurrencySelector from "../CurrencySelector";
import { LabelWembii } from "../../LabelWembii";
import { useBoardCore } from "../../../../cores/boards";
import { useMeCore } from "../../../../cores/me";
import { BoardType } from "../../../../cores/boards/constants/board-type.constant";
import { useGlobalCore } from "../../../../cores/globals";
import { BOARD_CREATE_CONFIG, BOARD_USERS_MODAL } from "../../../../constants/modal.constant";
import UserList from "./UserList";
import { UserBoardRole, UserBoardRoleLabels, UserBoardRoleOptions } from "../../../../cores/boards/constants/user-board-role";
import Avatar from "../../Avatar";
import { COLOR_DANGER } from "../../../../constants/theme";
import { UserRoles } from "../../../../cores/boards/interfaces/api/get-board";
import { Position } from "../../../../cores/globals/interfaces/globalEntity";
import { getFirstLetters } from "../../../utils/stringUtils";
import {
  CRM_BOARD_TEMPLATES,
  CrmBoardTemplateId,
} from "../../../../cores/boards/constants/crm-board-templates";
import { applyCrmBoardTemplate } from "../../../../cores/boards/helpers/applyCrmBoardTemplate";
import { toast } from "react-toastify";

interface Props {
  onSuccess?: (boardId?: string) => void;
  type: string;
}

const TEMPLATE_ICONS: Record<CrmBoardTemplateId, typeof faTableColumns> = {
  blank: faTableColumns,
  renewals: faCalendarDays,
};

export default function CreateBoard(props: Props) {
  const intl = useIntl();
  const { onSuccess, type } = props;
  const { turnOffModal, turnOnModal } = useGlobalCore();
  const { postBoard, postList } = useBoardCore();
  const { company, user } = useMeCore();
  const [name, setName] = useState("");
  const [color, setColor] = useState("#fff");
  const [currency, setCurrency] = useState("CLP");
  const [userRoles, setUserRoles] = useState<UserRoles[]>([]);
  const [templateId, setTemplateId] = useState<CrmBoardTemplateId>("blank");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onFormSubmit = async (e: any) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const createdBoard = await postBoard(
        company?.id!,
        name,
        color,
        currency,
        type,
        userRoles
      );

      if (!createdBoard?.id) return;

      if (type === BoardType.ACTIVITY) {
        const defaultColumns = [
          "Tareas Por hacer",
          "Tareas en Proceso",
          "Tareas Finalizadas",
        ];
        await Promise.all(
          defaultColumns.map((columnName) =>
            postList(company?.id!, columnName, createdBoard.id)
          )
        );
      }

      if (type === BoardType.BUSINESS && templateId !== "blank") {
        try {
          await applyCrmBoardTemplate(
            company?.id!,
            createdBoard.id,
            templateId,
            intl.locale || user?.locale
          );
        } catch (error) {
          console.error("applyCrmBoardTemplate error:", error);
          toast.error(
            intl.formatMessage({ id: "app.crmBusiness.template.applyError" })
          );
        }
      }

      onSuccess?.(createdBoard.id);
      turnOffModal(BOARD_CREATE_CONFIG);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTemplateSelect = useCallback(
    (nextTemplateId: CrmBoardTemplateId) => {
      setTemplateId(nextTemplateId);
      if (!name.trim()) {
        const template = CRM_BOARD_TEMPLATES.find((item) => item.id === nextTemplateId);
        if (template) {
          setName(intl.formatMessage({ id: template.defaultBoardNameKey }));
        }
      }
    },
    [intl, name]
  );

  const disabled = useCallback(
    () => !name || !currency || !color || isSubmitting,
    [name, currency, color, isSubmitting]
  );

  const getRole = useCallback((userRole: UserRoles) => 
    userRole.owner
      ? UserBoardRole.Owner
      : userRole.admin
        ? UserBoardRole.Admin
        : UserBoardRole.Member
  , []);

  const changeUserRole = useCallback((role: string, index: number) => {
    const newRoles = [...userRoles];
    newRoles[index] = {
      ...newRoles[index],
      admin: false,
      owner: false,
      member: false,
      [`${role}`]: true
    };
    setUserRoles(newRoles);
  }, [userRoles]);

  const removeUserRole = useCallback((userRole: UserRoles) => {
    setUserRoles(userRoles.filter((role) => role !== userRole));
  }, [userRoles]);

  const openAssignmentUsers = useCallback(() => {
    let users = userRoles.map((role) => role.user);
    users = [
      ...users, {
        id: user?.id!,
        lastName: user?.lastName!,
        firstName: user?.firstName!,
        status: user?.status!,
        avatarUrl: user?.avatarUrl!,
        email: user?.email!,
        phoneNumber: user?.phoneNumber!
      }
    ];
    turnOnModal(
      BOARD_USERS_MODAL,
      <UserList assignments={users} onAssign={(user) => {
        setUserRoles([
          ...userRoles,
          {
            id: "",
            owner: false,
            admin: false,
            member: true,
            user
          }
        ]);
        turnOffModal(BOARD_USERS_MODAL);
      }} />,
      Position.LEFT
    );
  }, [
    turnOnModal,
    turnOffModal,
    user,
    userRoles
  ]);

  return (
    <Container className="h-100" fluid>
      <div className="d-flex flex-column h-100 py-5">
        <h2 className="h2 text-center mb-4">
          {intl.formatMessage({ id: "app.activity.createBoard" })}
        </h2>
        <Form className="px-2" onSubmit={onFormSubmit}>
          {type === BoardType.BUSINESS && (
            <div className="crm-board-template-section mb-4">
              <LabelWembii className="mb-2">
                {intl.formatMessage({ id: "app.crmBusiness.template.title" })}
              </LabelWembii>
              <div className="crm-board-template-options">
                {CRM_BOARD_TEMPLATES.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    className={classNames("crm-board-template-option", {
                      "crm-board-template-option--active": templateId === template.id,
                    })}
                    onClick={() => handleTemplateSelect(template.id)}
                    aria-pressed={templateId === template.id}
                  >
                    <span className="crm-board-template-option__icon">
                      <FontAwesomeIcon icon={TEMPLATE_ICONS[template.id]} />
                    </span>
                    <span className="crm-board-template-option__label">
                      {intl.formatMessage({ id: template.nameKey })}
                    </span>
                    <span className="crm-board-template-option__hint">
                      {intl.formatMessage({ id: template.descriptionKey })}
                    </span>
                    {template.id === "renewals" && (
                      <span className="crm-board-template-option__meta">
                        {intl.formatMessage({
                          id: "app.crmBusiness.template.renewals.columnsPreview",
                        })}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <InputWembii
            type="text"
            name="boardName"
            id="boardName"
            label={intl.formatMessage({ id: "app.activity.boardName" })}
            value={name}
            required
            onChange={(v) => setName(v)}
          />

          {type === BoardType.BUSINESS && (
            <>
              <LabelWembii required={true}>
                {intl.formatMessage({ id: "app.crmBusiness.defaultCurrency" })}
              </LabelWembii>
              <CurrencySelector
                currency={currency}
                onSubmit={setCurrency}
                width="100%"
              />
            </>
          )}

          {type === BoardType.ACTIVITY && (
            <div className="d-flex align-items-end mb-4">
              <div 
                style={{ backgroundColor: color, height: 40, width: 70 }}
                className="rounded-pill shadow mr-2"
              />
              <InputWembii
                type="color"
                name="color"
                id="color"
                label={intl.formatMessage({ id: "app.activity.boardColor" })}
                mb={false}
                value={color}
                autoComplete="off"
                required
                onChange={(v) => setColor(v)}
              />
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center mt-3">
            <LabelWembii className="">
              {intl.formatMessage({ id: "app.crmBusiness.users" })}
            </LabelWembii>
            <Button
              className="whiteButton border m-0"
              onClick={openAssignmentUsers}
            >
              Add user
            </Button>
          </div>
          <Row className="mb-2">
            <Col className="d-flex align-items-center">
              <Avatar
                className="text-dark-50 pointer mr-2"
                image={user?.avatarUrl}
                label={getFirstLetters(`${user?.lastName} ${user?.firstName}`)}
                icon={
                  <FontAwesomeIcon
                    icon={["fas", "user"]}
                    size="1x"
                  />
                }
              />
              {user?.firstName} {user?.lastName}
            </Col>
            <Col className="d-flex align-items-center justify-content-center">
              {intl.formatMessage({ id: UserBoardRoleLabels[UserBoardRole.Owner] })}
            </Col>
          </Row>
          {userRoles.map((userRole, index) => (
            <Row className="mb-2" key={userRole.user.id || index}>
              <Col className="d-flex align-items-center">
                <Avatar
                  className="text-dark-50 pointer mr-2"
                  image={userRole.user.avatarUrl}
                  label={getFirstLetters(`${userRole.user.lastName} ${userRole.user.firstName}`)}
                  icon={
                    <FontAwesomeIcon
                      icon={["fas", "user"]}
                      size="1x"
                    />
                  }
                />
                {userRole.user.firstName} {userRole.user.lastName}
              </Col>
              <Col className="d-flex align-items-center">
                <InputWembii
                  type="select"
                  className="w-100"
                  mb={false}
                  value={getRole(userRole)}
                  onChange={(v) => changeUserRole(v, index)}
                >
                  {UserBoardRoleOptions.map(({ label, value }, index) => (
                    <option key={index} value={value}>
                      {intl.formatMessage({ id: label })}
                    </option>
                  ))}
                </InputWembii>
                <FontAwesomeIcon
                  icon={["fas", "trash"]}
                  className="m-3 pointer"
                  color={COLOR_DANGER}
                  onClick={() => removeUserRole(userRole)}
                />
              </Col>
            </Row>
          ))}
          
          <div className="d-flex justify-content-center mt-3">
            <Button color="primary" disabled={disabled()}>
              {isSubmitting && <Spinner size="sm" className="mr-2" />}
              {isSubmitting && templateId !== "blank" && type === BoardType.BUSINESS
                ? intl.formatMessage({ id: "app.crmBusiness.template.applying" })
                : intl.formatMessage({ id: "app.activity.accept" })}
            </Button>
          </div>
        </Form>
      </div>
    </Container>
  );
}
